import { apiFetch } from '../config/api'

export interface Brunch {
  id: number
  title: string
  slug: string
  subtitle: string
  event_date: string
  event_time: string
  price?: string | null
  description: string
  long_description: string
  image_url: string | null
  preview_image_url: string | null
  flyer_image_url: string | null
  icon_name: string
  features: string[]
  menu_highlights: string[]
  is_highlighted: boolean
  is_active: boolean
  is_featured?: boolean
}

export const brunchService = {
  async getAllBrunches(): Promise<Brunch[]> {
    try {
      const response = await apiFetch('/brunches')
      if (!response.ok) {
        console.error('API Response Error:', response.status, response.statusText)
        throw new Error('Failed to fetch brunches')
      }
      const data = await response.json()
      console.log('Brunches loaded:', data)
      const brunches = Array.isArray(data) ? data : []
      // Stelle sicher, dass immer Fallback-Bilder gesetzt sind
      return brunches.map(brunch => ({
        ...brunch,
        preview_image_url: brunch.preview_image_url || '/assets/brunch/brunch_vorschau_fallback.jpg',
        flyer_image_url: brunch.flyer_image_url || '/assets/brunch/brunch_flyer_fallback.jpg',
      }))
    } catch (error) {
      console.error('Error fetching brunches:', error)
      return []
    }
  },

  async getBrunchBySlug(slug: string): Promise<Brunch | null> {
    try {
      const brunches = await this.getAllBrunches()
      return brunches.find(b => b.slug === slug) || null
    } catch (error) {
      console.error('Error fetching brunch:', error)
      return null
    }
  },

  async getFeaturedBrunch(): Promise<Brunch | null> {
    try {
      const response = await apiFetch('/brunches?featured=1')
      if (!response.ok) {
        console.error('API Response Error:', response.status, response.statusText)
        return null
      }
      const data = await response.json()
      if (!data || data.error) {
        return null
      }
      // API kann einzelnes Objekt oder Array zurückgeben
      const brunch = Array.isArray(data) ? data[0] : data
      if (!brunch) {
        return null
      }
      // Stelle sicher, dass Fallback-Bilder gesetzt sind
      return {
        ...brunch,
        preview_image_url: brunch.preview_image_url || '/assets/brunch/brunch_vorschau_fallback.jpg',
        flyer_image_url: brunch.flyer_image_url || '/assets/brunch/brunch_flyer_fallback.jpg',
      }
    } catch (error) {
      console.error('Error fetching featured brunch:', error)
      return null
    }
  },

  async getNextBrunches(): Promise<Brunch[]> {
    try {
      const response = await apiFetch('/brunches?featured=1')
      if (!response.ok) {
        console.error('API Response Error:', response.status, response.statusText)
        return []
      }
      const data = await response.json()
      if (!data || data.error) {
        return []
      }
      // API kann einzelnes Objekt oder Array zurückgeben
      let brunches = Array.isArray(data) ? data : [data]
      
      // Stelle sicher, dass nur Brunches vom nächsten Datum zurückgegeben werden
      // Finde das früheste Datum
      if (brunches.length > 0) {
        const dates = brunches
          .map(b => b.event_date ? new Date(b.event_date).getTime() : Infinity)
          .filter(d => !isNaN(d) && d !== Infinity)
        
        if (dates.length > 0) {
          const minDate = Math.min(...dates)
          // Filtere nur Brunches am frühesten Datum
          brunches = brunches.filter(b => {
            if (!b.event_date) return false
            const brunchDate = new Date(b.event_date).getTime()
            return !isNaN(brunchDate) && brunchDate === minDate
          })
        }
      }
      
      // Stelle sicher, dass Fallback-Bilder gesetzt sind
      return brunches.map(brunch => ({
        ...brunch,
        preview_image_url: brunch.preview_image_url || '/assets/brunch/brunch_vorschau_fallback.jpg',
        flyer_image_url: brunch.flyer_image_url || '/assets/brunch/brunch_flyer_fallback.jpg',
      }))
    } catch (error) {
      console.error('Error fetching next brunches:', error)
      return []
    }
  }
}
